import type { PrivacyCenterTrackingPurposePreview } from '@main/cm-types';
import {
  addQueryParams,
  ErrorText,
  FlexColumnCenterVertical,
  FlexRowCenterVertical,
  SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY,
  StyleUtils,
  useRouter,
} from '@main/core-ui';
import { ImmutableUrl } from '@main/immutable-url';
import { A } from '@main/pc-core-components';
import { PAGE_PATHNAMES } from '@main/pc-types';
import {
  StyledPrimaryButton,
  StyledSecondaryButton,
} from '@main/privacy-center/src/components/Card/wrappers';
import { sp } from '@main/theme';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  useChildOrganizationPreferences,
  useLoginRedirection,
} from '../../hooks';
import { PurposeList } from './components';
import { ChildOrganizationHeader } from './components/ChildOrganizationHeader';
import { communicationsPreferencesMessages } from './messages';

interface PreviewPreferencesProps {
  /** Configured Purposes */
  purposes: PrivacyCenterTrackingPurposePreview[];
}

/**
 * A component that allows previewing purposes configured by the organization
 */
export const PreviewPreferences: React.FC<
  React.PropsWithChildren<PreviewPreferencesProps>
> = ({ purposes: configuredPurposes }) => {
  const {
    isDraft,
    parentPrivacyCenter,
    childOrganizations,
    organization,
    logo,
  } = useChildOrganizationPreferences(true);

  const { formatMessage } = useIntl();
  const { location, redirect } = useRouter();
  const redirectTo = useLoginRedirection();
  redirectTo({ key: 'redirectTo', value: 'log-in' }, 'modal=log-in');

  const filteredPurposes = useMemo(
    () =>
      configuredPurposes.filter(
        ({ showInPrivacyCenter }) => showInPrivacyCenter,
      ),
    [configuredPurposes],
  );
  return (
    <>
      <FlexRowCenterVertical style={{ gap: StyleUtils.Spacing.md }}>
        <StyledSecondaryButton
          type="reset"
          buttonType="secondary"
          onClick={() =>
            redirect(
              addQueryParams(location, {
                modal: 'select-subject',
                redirectTo: `${location.pathname}?redirectTo=log-in`,
              }),
            )
          }
        >
          {formatMessage(communicationsPreferencesMessages.signIn)}
        </StyledSecondaryButton>
        {parentPrivacyCenter && (
          <A
            href={
              new ImmutableUrl(parentPrivacyCenter.url).transform({
                pathname: PAGE_PATHNAMES.COMMUNICATIONS_PREFERENCES,
                ...(isDraft
                  ? {
                      searchTransform: (qs) => ({
                        ...qs,
                        [SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY]: true,
                      }),
                    }
                  : {}),
              }).href
            }
          >
            <StyledPrimaryButton type="reset" buttonType="primary">
              {formatMessage(
                communicationsPreferencesMessages.goToParentPrivacyCenter,
              )}
            </StyledPrimaryButton>
          </A>
        )}
      </FlexRowCenterVertical>
      <FlexColumnCenterVertical style={{ gap: sp(1.5) }}>
        {childOrganizations.length > 0 ? (
          filteredPurposes.length > 0 ? (
            <div>
              <ChildOrganizationHeader logo={logo} name={organization.name} />
              <div style={{ padding: '15px' }}>
                <PurposeList
                  showPreferenceTopics={false}
                  disabled
                  purposes={filteredPurposes}
                />
              </div>
            </div>
          ) : null
        ) : (
          <PurposeList disabled purposes={filteredPurposes} />
        )}
        {childOrganizations
          .filter((org) => org.purposes)
          .map((org) => (
            <div key={org.id}>
              <ChildOrganizationHeader
                logo={org.logo}
                name={org.name}
                url={
                  new ImmutableUrl(org.deployedPrivacyCenterUrl).transform({
                    pathname: PAGE_PATHNAMES.COMMUNICATIONS_PREFERENCES,
                    ...(isDraft
                      ? {
                          searchTransform: (qs) => ({
                            ...qs,
                            [SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY]: true,
                          }),
                        }
                      : {}),
                  }).href
                }
              />
              <div style={{ padding: '15px' }}>
                {org.failedToLoad ? (
                  <ErrorText>
                    {formatMessage(
                      communicationsPreferencesMessages.failedToLoadPurposes,
                    )}
                  </ErrorText>
                ) : (
                  <PurposeList
                    showPreferenceTopics={false}
                    disabled
                    purposes={org.purposes!.filter(
                      (purpose) => purpose.showInPrivacyCenter,
                    )}
                  />
                )}
              </div>
            </div>
          ))}
      </FlexColumnCenterVertical>
    </>
  );
};
